import * as React from 'react';
import type { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import type { Immutable } from 'immer';
import { produce } from 'immer';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { client } from '@studio/api/api-client/client';
import { isAxiosError } from '@studio/api/api-client/is-axios-error';
import { isCancel } from '@studio/api/api-client/is-cancel';
import { selectAccessToken, selectCustomerId } from '@studio/store';
import type { HttpMethods, ApiClientConfig } from '@studio/types/axios';

const DEFAULT_TIME_DELAY = 60_000;

/** Cross-component interval registry (dedupe + cancellation) */
const intervalRegistry = new Map<
  string,
  { controller: AbortController; refCount: number; timer: ReturnType<typeof setTimeout> | null }
>();

export const stableStringify = (obj: unknown): string =>
  JSON.stringify(obj, Array.isArray(obj) ? undefined : Object.keys(obj as Record<string, unknown>).sort());

const makeIntervalKey = (req: { data: unknown; params: unknown; url: string }) => stableStringify(req);

const initialState = {
  hasFetched: false,
  isFetching: false,
  isFetchingInterval: false,
  isFetchingMore: false,
  error: null as AxiosError | Error | null,
  data: undefined as unknown,
  mutatedData: undefined as unknown,
  responseHeaders: undefined as unknown,
};

type _State<T, M> = {
  data?: T;
  error: AxiosError | Error | null;
  hasFetched: boolean;
  isFetching: boolean;
  isFetchingInterval: boolean;
  isFetchingMore: boolean;
  mutatedData?: M;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responseHeaders?: any;
  status?: number;
};

type _Actions<T, M> =
  | { payload: AxiosError | Error | null; type: 'FETCH_ERROR' }
  | { payload: AxiosResponse<T> & { mutatedData?: M }; type: 'FETCHED_MORE' }
  | { payload: AxiosResponse<T> & { mutatedData?: M }; type: 'FETCHED' }
  | { payload: T; type: 'SET_DATA' }
  | { payload?: boolean; type: 'FETCHING' }
  | { type: 'CANCEL_INTERVAL' }
  | { type: 'CLEAR_DATA' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'FETCHING_INTERVAL' }
  | { type: 'FETCHING_MORE' };

type _CustomerIdOnly = { customerId?: string };

/** Same as Nullable except without `null`. */
type _Optional<T> = T | undefined;
/** Types that can be used to index native JS types */
type _IndexSignature = number | string | symbol;
/** Indexable object helper */
// eslint-disable-next-line @typescript-eslint/ban-types
type _Obj<O extends Record<_IndexSignature, unknown> | object = Record<_IndexSignature, unknown> | object> = Omit<
  O,
  never
> & {
  [K in keyof O as K extends never ? never : K]: K extends never ? never : O[K] extends never ? never : O[K];
};
/** Any string/number/symbol-indexable type */
type _Indexable<ValueTypes = unknown> =
  | _Obj
  | {
      [K: _IndexSignature]: ValueTypes;
    };
/** Pick only optional properties (optionally deep) */
type _PickOptional<T, DEEP extends boolean = false> = {
  [K in keyof T as undefined extends T[K] ? K : never]: DEEP extends false
    ? T[K]
    : T[K] extends _Optional<_Indexable>
      ? _PickOptional<T[K], DEEP>
      : T[K];
};

type _PickRequired<T, DEEP extends boolean = false> = {
  [K in keyof T as K extends keyof _PickOptional<T, DEEP> ? never : K]: T[K] extends _Indexable
    ? _PickRequired<T[K], DEEP>
    : T[K];
};

type _Params<T> =
  T extends _PickRequired<T>
    ? { params: Required<Omit<T, 'customerId'>> & { customerId?: string } }
    : { params?: _CustomerIdOnly };

type _Query<T> = T extends _PickRequired<T> ? { query: T } : { query?: T };

// ---- Public API types

type _CallAPI<D, P, Q, R, H, T extends boolean = false> = (
  args?: D,
  optionalParams?: {
    clearData?: boolean;
    headers?: H;
    params?: Partial<P>;
    query?: Q;
    signal?: AbortSignal;
    silent?: boolean;
  }
) => T extends true ? Promise<AxiosResponse<R>> : Promise<R>;

/** Handle returned by callInterval */
export type IntervalHandle = {
  dispose: () => void;
  intervalKey: string;
};

type _CallAPIInterval<D, P, Q, _R, H, _T extends boolean = false> = (
  args?: D,
  optionalParams?: {
    autoDispose?: boolean;
    clearData?: boolean;
    headers?: H;
    interval?: number;
    params?: Partial<P>;
    query?: Q;
    signal?: AbortSignal;
  }
) => Promise<IntervalHandle | undefined>;

type _MethodReturns<D, P, Q, R, H, T extends boolean = false> = {
  callApi: _CallAPI<D, P, Q, R, H, T>;
  callInterval: _CallAPIInterval<D, P, Q, R, H, T>;
};

type _HeadersMethod<D, P, Q, R, H, T extends boolean = false> = {
  headers: (
    headers: H
  ) => _MethodReturns<D, P, Q, R, H, T> & _ParamsMethod<D, P, Q, R, H, T> & _QueryMethod<D, P, Q, R, H, T>;
};
type _IntervalMethod<D, P, Q, R, H, T extends boolean = false> = {
  interval: (
    ms: number
  ) => _HeadersMethod<D, P, Q, R, H, T> &
    _MethodReturns<D, P, Q, R, H, T> &
    _ParamsMethod<D, P, Q, R, H, T> &
    _QueryMethod<D, P, Q, R, H, T>;
};
type _QueryMethod<D, P, Q, R, H, T extends boolean = false> = {
  query: (
    queryParams: Q
  ) => _HeadersMethod<D, P, Q, R, H, T> &
    _IntervalMethod<D, P, Q, R, H, T> &
    _MethodReturns<D, P, Q, R, H, T> &
    _ParamsMethod<D, P, Q, R, H, T>;
};
type _ParamsMethod<D, P, Q, R, H, T extends boolean = false> = {
  params: (
    pathParams: Partial<P>
  ) => _HeadersMethod<D, P, Q, R, H, T> &
    _IntervalMethod<D, P, Q, R, H, T> &
    _MethodReturns<D, P, Q, R, H, T> &
    _QueryMethod<D, P, Q, R, H, T>;
};

export type _ReturnType<D, M, P, Q, R, H, T extends boolean = false> = _HeadersMethod<D, P, Q, R, H, T> &
  _IntervalMethod<D, P, Q, R, H, T> &
  _MethodReturns<D, P, Q, R, H, T> &
  _ParamsMethod<D, P, Q, R, H, T> &
  _QueryMethod<D, P, Q, R, H, T> &
  Immutable<_State<R, M>> & {
    callNextPage: () => Promise<R | undefined>;
    cancelInterval: () => void;
    clearError: () => void;
    /** If you know the intervalKey, get a disposer for it. */
    createIntervalDisposer: (intervalKey: string) => () => void;
    /** For paginated accumulation */
    pageData: Map<string, R>;
    pageUrl?: string;
    setData: (data: R) => void;
  };

// ---- Reducer

function createReducer<T, M>(state: _State<T, M>, action: _Actions<T, M>) {
  return produce(state, (draft: _State<T, M>) => {
    switch (action.type) {
      case 'FETCHING':
        draft.isFetchingInterval = Boolean(action.payload);
        draft.isFetching = true;
        draft.error = null;
        break;
      case 'FETCHING_MORE':
        draft.isFetchingMore = true;
        draft.error = null;
        break;
      case 'CLEAR_DATA':
        draft.data = undefined;
        draft.mutatedData = undefined;
        draft.responseHeaders = undefined;
        draft.error = null;
        draft.status = undefined;
        break;
      case 'CLEAR_ERROR':
        draft.error = null;
        break;
      case 'CANCEL_INTERVAL':
        draft.isFetchingInterval = false;
        break;
      case 'FETCHING_INTERVAL':
        draft.error = null;
        draft.isFetchingInterval = true;
        break;
      case 'FETCHED':
        draft.data = action.payload.data;
        draft.mutatedData = action.payload.mutatedData;
        draft.responseHeaders = action.payload.headers;
        draft.status = action.payload.status;
        draft.error = null;
        draft.isFetching = false;
        if (!draft.hasFetched) draft.hasFetched = true;
        break;
      case 'FETCHED_MORE':
        draft.data = action.payload.data;
        draft.mutatedData = action.payload.mutatedData;
        draft.responseHeaders = action.payload.headers;
        draft.error = null;
        draft.status = action.payload.status;
        draft.isFetchingMore = false;
        break;
      case 'SET_DATA':
        draft.data = action.payload;
        break;
      case 'FETCH_ERROR':
        if (!isCancel(action.payload)) {
          draft.error = action.payload;
          if (isAxiosError(action.payload)) {
            draft.status = action.payload.response?.status;
          }
        }
        draft.isFetching = false;
        draft.isFetchingMore = false;
        break;
      /* istanbul ignore next */
      default:
        break;
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const paramsSerializer = (paramsToSerialize: any, paramOptions?: qs.IStringifyOptions): string =>
  qs.stringify(paramsToSerialize, paramOptions ?? { encode: false });

export default function useClient<D, M, R, P, Q, H, T extends boolean = false>({
  config,
  dataPrep,
  headers: headersProp,
  interval: intervalProp,
  mutateData,
  params: paramsProp,
  parsePageUrl,
  query: queryProp,
  returnAxiosResponse,
  token,
  ...otherAxiosApiClientConfig
}: _Params<P> &
  _Query<Q> &
  ApiClientConfig & {
    config: {
      headers?: H;
      method: HttpMethods;
      paramOptions?: qs.IStringifyOptions;
      params?: P;
      query?: Q;
      request?: D;
      response?: R;
      url: string;
    };
    dataPrep?: (d: R) => R;
    headers?: H;
    interval?: number;
    mutateData?: (data: R, previousPageData?: R[]) => M;
    parsePageUrl?: (response: R, headers: AxiosResponse<R>['headers'], url: string) => string | undefined;
    returnAxiosResponse?: T;
    token?: string;
  }): _ReturnType<D, M, P, Q, R, H, T> {
  const { method, paramOptions, query, url } = config;
  const otherAxiosConfigRef = React.useRef(otherAxiosApiClientConfig);
  const mounted = React.useRef(false);
  const authToken = useSelector(selectAccessToken) ?? '';
  const customerId = useSelector(selectCustomerId) ?? '';
  const pageData = React.useRef(new Map<string, R>());
  const pageUrl = React.useRef<string | undefined>();
  const queryParams = React.useRef(queryProp ?? query);
  const pathParams = React.useRef(paramsProp);
  const reqHeaders = React.useRef(headersProp);
  const intervalRef = React.useRef(intervalProp || DEFAULT_TIME_DELAY);
  // Track interval handles created by this hook instance for auto-cleanup
  const activeIntervals = React.useRef<Set<() => void>>(new Set());

  const newUrl = React.useCallback(
    (p?: Partial<P>) => {
      const paramsToProcess = {
        customerId,
        ...(pathParams.current ?? {}),
        ...(p ?? {}),
      };

      return Object.keys(paramsToProcess).reduce((agg, paramToProcess) => {
        const regex = new RegExp(`@${paramToProcess}`, 'g');
        return agg.replace(regex, paramsToProcess[paramToProcess as keyof typeof paramsToProcess]);
      }, url);
    },
    [customerId, pathParams, url]
  );

  const [state, dispatch] = React.useReducer(createReducer<R, M>, initialState as _State<R, M>);

  const getSharedAxiosConfig = React.useCallback(
    () => ({
      method,
      token: token ?? authToken,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      paramsSerializer: (params: any) => paramsSerializer(params, paramOptions),
      returnResponse: true,
      ...otherAxiosConfigRef.current,
    }),
    [authToken, method, paramOptions, token]
  );

  const callNextPage = React.useCallback(async () => {
    if (!pageUrl.current) return;
    if (mounted.current) dispatch({ type: 'FETCHING_MORE' });

    try {
      const response = await client<AxiosResponse<R>>(pageUrl.current, {
        headers: reqHeaders.current,
        ...getSharedAxiosConfig(),
      });

      const dataToDispatch = dataPrep ? dataPrep(response.data) : response.data;
      const prevState = pageData.current.size ? Array.from(pageData.current.values()).reverse() : undefined;

      if (mounted.current) {
        dispatch({
          type: 'FETCHED_MORE',
          payload: {
            ...response,
            data: dataToDispatch,
            mutatedData: mutateData?.(dataToDispatch, prevState),
          },
        });
        pageData.current.set(pageUrl.current, dataToDispatch);
        pageUrl.current = parsePageUrl?.(response.data, response.headers, axios.getUri(response.config));
      }

      if (returnAxiosResponse) {
        return { ...response, data: dataToDispatch };
      } else {
        return dataToDispatch;
      }
    } catch (err) {
      if (mounted.current && err instanceof Error) {
        dispatch({ type: 'FETCH_ERROR', payload: err });
      }
      throw err;
    }
  }, [getSharedAxiosConfig, dataPrep, returnAxiosResponse, mutateData, parsePageUrl]);

  const callApi = React.useCallback(
    async (
      payload?: D,
      optionalParams?: {
        clearData?: boolean;
        headers?: H;
        params?: Partial<P>;
        query?: Q;
        signal?: AbortSignal;
        silent?: boolean;
      }
    ) => {
      // Check if this hook instance has any active intervals
      const hasActiveIntervals = activeIntervals.current.size > 0;
      !optionalParams?.silent && dispatch({ type: 'FETCHING', payload: hasActiveIntervals });
      if (optionalParams?.clearData) {
        pageUrl.current = undefined;
        pageData.current.clear();
        dispatch({ type: 'CLEAR_DATA' });
      }

      try {
        const response = await client<AxiosResponse<R>>(newUrl(optionalParams?.params), {
          data: payload,
          headers: { ...reqHeaders.current, ...optionalParams?.headers },
          params: { ...queryParams.current, ...optionalParams?.query },
          signal: optionalParams?.signal,
          ...getSharedAxiosConfig(),
        });

        const dataToDispatch = dataPrep ? dataPrep(response.data) : response.data;
        const prevState = pageData.current.size ? Array.from(pageData.current.values()).reverse() : undefined;

        if (mounted.current) {
          dispatch({
            type: 'FETCHED',
            payload: {
              ...response,
              data: dataToDispatch,
              mutatedData: mutateData?.(dataToDispatch, optionalParams?.clearData ? [] : prevState),
            },
          });
          pageUrl.current = parsePageUrl?.(response.data, response.headers, axios.getUri(response.config));
          pageData.current.set(axios.getUri(response.config), dataToDispatch);
        }

        if (returnAxiosResponse) {
          return { ...response, data: dataToDispatch };
        } else {
          return dataToDispatch;
        }
      } catch (err) {
        if (mounted.current && err instanceof Error) {
          dispatch({ type: 'FETCH_ERROR', payload: err });
        }
        throw err;
      }
    },
    [newUrl, getSharedAxiosConfig, dataPrep, returnAxiosResponse, mutateData, parsePageUrl]
  );

  const callInterval = React.useCallback(
    async (
      payload?: D,
      optionalParams?: {
        autoDispose?: boolean;
        clearData?: boolean;
        headers?: H;
        interval?: number;
        params?: Partial<P>;
        query?: Q;
        signal?: AbortSignal;
      }
    ): Promise<IntervalHandle | undefined> => {
      // Build the dedupe key for this interval
      const requestConfig = {
        data: payload as unknown,
        params: { ...queryParams.current, ...optionalParams?.query },
        url: newUrl(optionalParams?.params),
      };
      const intervalKey = makeIntervalKey(requestConfig);

      // If already active, just bump refCount and return a disposer for *this* subscriber
      const existingEntry = intervalRegistry.get(intervalKey);
      if (existingEntry) {
        existingEntry.refCount++;

        // Mark THIS hook as interval-fetching immediately
        if (mounted.current) {
          dispatch({ type: 'FETCHING_INTERVAL' });
        }

        const dispose = () => {
          const e = intervalRegistry.get(intervalKey);
          if (!e) return;
          e.refCount = Math.max(0, e.refCount - 1);
          if (e.refCount === 0) {
            if (e.timer) clearTimeout(e.timer);
            e.controller.abort();
            intervalRegistry.delete(intervalKey);
          }
          activeIntervals.current.delete(dispose);
          if (mounted.current && activeIntervals.current.size === 0) {
            dispatch({ type: 'CANCEL_INTERVAL' });
          }
        };

        // Auto-track for cleanup unless explicitly disabled
        if (optionalParams?.autoDispose !== false) {
          activeIntervals.current.add(dispose);
        }

        return { intervalKey, dispose };
      }

      // Create a new entry
      const controller = new AbortController();
      const entry = { controller, refCount: 1, timer: null as ReturnType<typeof setTimeout> | null };
      intervalRegistry.set(intervalKey, entry);

      const scheduleNext = (ms: number) => {
        const next = Math.max(16, ms | 0); // minimum ~1 frame
        if (!intervalRegistry.has(intervalKey)) return;
        const e = intervalRegistry.get(intervalKey);
        if (!e || e.controller.signal.aborted) return;
        if (e.timer) clearTimeout(e.timer);
        e.timer = setTimeout(() => {
          if (intervalRegistry.get(intervalKey) !== e) return;
          tick();
        }, next);
      };

      const tick = async () => {
        const curr = intervalRegistry.get(intervalKey);
        if (!curr || curr.controller.signal.aborted) return;

        if (curr.timer) {
          dispatch({ type: 'FETCHING_INTERVAL' });
        } else {
          dispatch({ type: 'FETCHING', payload: true });
        }

        if (optionalParams?.clearData) {
          pageUrl.current = undefined;
          pageData.current.clear();
          dispatch({ type: 'CLEAR_DATA' });
        }

        try {
          const response = await client<AxiosResponse<R>>(requestConfig.url, {
            data: payload,
            headers: { ...reqHeaders.current, ...optionalParams?.headers },
            params: requestConfig.params,
            signal: optionalParams?.signal || curr.controller.signal,
            ...getSharedAxiosConfig(),
          });

          if (!mounted.current) return;

          // schedule next only if entry still active
          const stillActive = intervalRegistry.get(intervalKey) === curr;

          if (stillActive) {
            const intervalMs = optionalParams?.interval ?? intervalRef.current;
            scheduleNext(intervalMs);
          }

          const dataToDispatch = dataPrep ? dataPrep(response.data) : response.data;
          const prevState = pageData.current.size ? Array.from(pageData.current.values()).reverse() : undefined;

          dispatch({
            type: 'FETCHED',
            payload: {
              ...response,
              data: dataToDispatch,
              mutatedData: mutateData?.(dataToDispatch, optionalParams?.clearData ? undefined : prevState),
            },
          });

          if (returnAxiosResponse) {
            // return value is not used by callers; polling updates state
            // we keep the path for parity but ignore here
          }
        } catch (err) {
          if (mounted.current && err instanceof Error) {
            dispatch({ type: 'FETCH_ERROR', payload: err });
          }
          // keep polling unless disposed; re-check membership before scheduling
          const curr2 = intervalRegistry.get(intervalKey);
          if (mounted.current && curr2 && curr2 === entry) {
            scheduleNext(optionalParams?.interval ?? intervalRef.current);
          }
        }
      };

      await tick();

      const dispose = () => {
        const e = intervalRegistry.get(intervalKey);
        if (!e) return;
        e.refCount = Math.max(0, e.refCount - 1);
        if (e.refCount === 0) {
          if (e.timer) clearTimeout(e.timer);
          e.controller.abort();
          intervalRegistry.delete(intervalKey);
        }
        // Remove from auto-cleanup tracking
        activeIntervals.current.delete(dispose);

        // If this hook instance has no more active intervals, clear its fetching state
        if (mounted.current && activeIntervals.current.size === 0) {
          dispatch({ type: 'CANCEL_INTERVAL' });
        }
      };

      // Auto-track for cleanup unless explicitly disabled
      if (optionalParams?.autoDispose !== false) {
        activeIntervals.current.add(dispose);
      }

      return { intervalKey, dispose };
    },
    [newUrl, getSharedAxiosConfig, dataPrep, mutateData, returnAxiosResponse]
  );

  const setData = React.useCallback((dataToSet: R) => {
    dispatch({ type: 'SET_DATA', payload: dataToSet });
  }, []);

  const setters = React.useMemo(
    () => ({
      headers: (h: H) => {
        reqHeaders.current = { ...reqHeaders.current, ...h } as typeof reqHeaders.current;
        return { callApi, callInterval, query: setters.query, params: setters.params, interval: setters.interval };
      },
      interval: (i: number) => {
        intervalRef.current = i;
        return { callApi, callInterval, query: setters.query, params: setters.params, headers: setters.headers };
      },
      params: (p: Partial<P>) => {
        pathParams.current = p;
        return { callApi, callInterval, query: setters.query, interval: setters.interval, headers: setters.headers };
      },
      query: (q: Q) => {
        queryParams.current = q;
        return { callApi, callInterval, params: setters.params, interval: setters.interval, headers: setters.headers };
      },
    }),
    [callApi, callInterval]
  );

  const cancelInterval = React.useCallback(() => {
    for (const dispose of Array.from(activeIntervals.current)) {
      dispose();
    }
    activeIntervals.current.clear();

    if (mounted.current) {
      dispatch({ type: 'CANCEL_INTERVAL' });
    }
  }, []);

  const clearError = React.useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const createIntervalDisposer = React.useCallback((intervalKey: string) => {
    return () => {
      const entry = intervalRegistry.get(intervalKey);
      if (!entry) return;
      entry.refCount = Math.max(0, entry.refCount - 1);
      if (entry.refCount === 0) {
        if (entry.timer) clearTimeout(entry.timer);
        entry.controller.abort();
        intervalRegistry.delete(intervalKey);
        if (mounted.current) dispatch({ type: 'CANCEL_INTERVAL' });
      }
    };
  }, []);

  React.useEffect(() => {
    if (!mounted.current) mounted.current = true;
    const activeIntervalsRef = activeIntervals.current;
    return () => {
      for (const dispose of activeIntervalsRef) {
        dispose();
      }
      activeIntervalsRef.clear();

      mounted.current = false;
    };
  }, []);

  const values = React.useMemo(
    () => ({
      callApi,
      callInterval,
      callNextPage,
      cancelInterval,
      clearError,
      createIntervalDisposer,
      headers: setters.headers,
      interval: setters.interval,
      params: setters.params,
      query: setters.query,
      setData,
      pageData: pageData.current,
      pageUrl: pageUrl.current,
      ...state,
    }),
    [
      callApi,
      callInterval,
      callNextPage,
      cancelInterval,
      clearError,
      createIntervalDisposer,
      setData,
      setters.headers,
      setters.interval,
      setters.params,
      setters.query,
      state,
    ]
  );

  return values as _ReturnType<D, M, P, Q, R, H, T>;
}
